import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { addWeeks, format, startOfWeek } from "date-fns";
import { Clock } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { Skeleton } from "@/components/ui/skeleton";
import {
  apiGetTimesheetWeek, apiEnsureTimesheetWeek, apiSaveTimesheetEntries,
  apiSubmitTimesheet, apiCopyPreviousWeek, apiGetTimekeepingTasks, apiGetTimekeepingSettings,
  type TimesheetWeek, type TimesheetEntryInput,
} from "@/lib/api";
import { WeekFlow } from "@/components/timekeeping/WeekFlow";
import { getTimekeepingErrorMessage } from "@/lib/timekeeping-errors";

const toISO = (d: Date) => format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

export default function Timekeeping() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [anchor, setAnchor] = useState<Date>(new Date());
  const weekISO = toISO(anchor);

  const { data: week, isLoading } = useQuery<TimesheetWeek>({
    queryKey: ["timesheet-week", weekISO],
    queryFn: () => apiGetTimesheetWeek(weekISO),
  });
  const { data: tasks = [] } = useQuery({
    queryKey: ["timekeeping-tasks"],
    queryFn: () => apiGetTimekeepingTasks(false),
  });
  const { data: settings } = useQuery({
    queryKey: ["timekeeping-settings"],
    queryFn: apiGetTimekeepingSettings,
  });

  const ensureId = async (): Promise<number> => {
    if (week?.timesheetId != null) return week.timesheetId;
    const ensured = await apiEnsureTimesheetWeek(weekISO);
    if (ensured.timesheetId == null) throw new Error(t("timekeeping.errors.createWeekFailed", "The week could not be created."));
    return ensured.timesheetId;
  };

  const afterMutate = (updated: TimesheetWeek) => qc.setQueryData(["timesheet-week", weekISO], updated);

  const saveMut = useMutation({
    mutationFn: async (entries: TimesheetEntryInput[]) => apiSaveTimesheetEntries(await ensureId(), entries),
    onSuccess: (u) => {
      afterMutate(u);
      toast.success(t("timekeeping.week.saved", { defaultValue: "Draft saved" }));
      if (u.warnings?.includes("under_effective_required_hours")) {
        toast.warning(t("timekeeping.submit.underHoursWarning", { defaultValue: "Logged hours are below the weekly requirement." }));
      }
    },
    onError: (e) => toast.error(getTimekeepingErrorMessage(e, t)),
  });

  const submitMut = useMutation({
    mutationFn: async (entries: TimesheetEntryInput[]) => {
      const id = await ensureId();
      await apiSaveTimesheetEntries(id, entries);
      return apiSubmitTimesheet(id);
    },
    onSuccess: (u) => { afterMutate(u); toast.success(t("timekeeping.submit.done", { defaultValue: "Week submitted for approval" })); },
    onError: (e) => toast.error(getTimekeepingErrorMessage(e, t)),
  });

  const copyMut = useMutation({
    mutationFn: async () => apiCopyPreviousWeek(await ensureId(), { mode: "all", includeNotes: true }),
    onSuccess: (u) => { afterMutate(u); toast.success(t("timekeeping.copy.done", { defaultValue: "Previous week copied" })); },
    onError: (e) => toast.error(getTimekeepingErrorMessage(e, t)),
  });

  const editable = !!week && (week.status === "Draft" || week.status === "Needs Changes");

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center">
            <Clock className="h-5 w-5 text-indigo-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("timekeeping.title", { defaultValue: "Timekeeping" })}</h1>
            <p className="text-sm text-muted-foreground">
              {t("timekeeping.subtitle", { defaultValue: "Record your working time and submit weekly timesheets." })}
            </p>
          </div>
        </div>

        {(week?.status === "Rejected" && week.rejectReason) && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-2 text-sm text-red-600 dark:text-red-400">
            {t("timekeeping.status.rejected", { defaultValue: "Rejected" })}: {week.rejectReason}
          </div>
        )}
        {(week?.status === "Needs Changes" && week.returnReason) && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-sm text-amber-600 dark:text-amber-400">
            {t("timekeeping.status.needsChanges", { defaultValue: "Needs changes" })}: {week.returnReason}
          </div>
        )}

        {isLoading || !week ? (
          <Skeleton className="h-96 w-full rounded-2xl" />
        ) : (
          <WeekFlow
            week={week}
            tasks={tasks}
            editable={editable}
            saving={saveMut.isPending}
            submitting={submitMut.isPending}
            maxDailyHours={settings?.maxDailyHours}
            allowFutureDates={settings?.allowFutureDates}
            onPrevWeek={() => setAnchor(addWeeks(anchor, -1))}
            onNextWeek={() => setAnchor(addWeeks(anchor, 1))}
            onThisWeek={() => setAnchor(new Date())}
            onSaveDraft={(entries) => saveMut.mutate(entries)}
            onSubmit={(entries) => submitMut.mutate(entries)}
            onCopyPrevious={() => copyMut.mutate()}
          />
        )}
      </div>
    </AppLayout>
  );
}
